/**
 * React Query hooks for the damage-analysis API.
 *
 * Backend routes are under /api/0/reservation/:id/damage-analysis*.
 * The mutation runs the analysis (slow: 20–60 s, calls Gemini); the
 * read queries are cheap (just activity-log lookups).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { get, post } from '../fetch'

export type DamageType =
	| 'scratch'
	| 'dent'
	| 'scuff'
	| 'crack'
	| 'chip'
	| 'missing_part'
	| 'dirt_stain'
	| 'other'

export type DamageAngle =
	| 'front'
	| 'left_side'
	| 'rear'
	| 'right_side'
	| 'interior'
	| 'trunk'
	| 'extra'
	| ''

export type DamageBbox = {
	x: number // 0..1, top-left of the box, relative to "after" photo
	y: number
	w: number
	h: number
}

export type DamageItem = {
	location: string
	angle: DamageAngle
	type: DamageType
	severity: 1 | 2 | 3
	confidence: number
	note: string
	photo_index: number // 1-based index into 'after' photo array; 0 = unknown
	bbox: DamageBbox | null // null when model didn't return a valid box
}

export type DamageModelAttempt = {
	model: string
	key_index: number
	status: number
	reason: string
	message?: string // server-side: short Gemini error message, if any
	duration_ms?: number // server-side: how long this attempt took
}

/**
 * Temporary diagnostics ("co proběhlo přes API"). Attached to the report on
 * success and to the ServerError.extra on failure. Rendered by DebugPanel in
 * damage-analysis.tsx. Safe to remove once the Gemini integration is stable.
 */
export type DamagePhotoDebug = {
	index: number
	side: 'before' | 'after'
	url: string
	status: 'pending' | 'ok' | 'unresolved' | 'fetch_error' | 'http_error' | 'too_large'
	http_status?: number
	bytes?: number
	source_mime?: string
	final_mime?: string
	final_bytes?: number
	resized?: boolean
	width?: number
	height?: number
	error?: string
}

export type DamageDebug = {
	note: string
	generated_at: string
	sharp_available: boolean
	config: {
		max_photos: number
		max_dim_px: number
		max_bytes: number
		multi_pass: boolean
		model_primary: string
		model_chain: string[]
		key_count: number
	}
	source_request_ids: { handover: string; return: string }
	photos: {
		before_total: number
		after_total: number
		before_loaded: number
		after_loaded: number
		paired: number
		before: DamagePhotoDebug[]
		after: DamagePhotoDebug[]
	}
	payload: {
		estimated_bytes: number
		image_part_count: number
		limit_bytes: number
		shrink_passes: Array<{ target_dim: number; estimated_bytes: number }>
	} | null
	attempts: DamageModelAttempt[]
	used: { model: string; key_index: number; model_fallback_used: boolean } | null
	voice_notes: { found: number; transcribed: number }
	total_duration_ms: number
}

export type DamageReport = {
	version: number
	reservation_id: string
	model: string
	model_primary: string
	model_fallback_used: boolean
	model_attempts: DamageModelAttempt[]
	key_index: number
	created_at: string
	duration_ms: number
	counts: {
		before_total: number
		before_loaded: number
		after_total: number
		after_loaded: number
		paired?: number // v2+: angles where BOTH before & after photos loaded
	}
	min_confidence_filter: number
	source_request_ids: { handover: string; return: string }
	before_photos?: string[] // v2+: URLs of handover photos
	after_photos?: string[] // v2+: URLs of return photos (photo_index references these)
	multi_pass_used?: boolean // v2+: was second pass run?
	multi_pass_dropped?: number // damages dropped because second pass disagreed
	second_pass_model?: string | null
	voice_transcripts?: Array<{
		source: 'handover' | 'return'
		url: string
		duration_seconds: number | null
		created_at: string | null
		transcript: string | null
	}>
	summary: string
	damages: DamageItem[]
	activity_log_id?: string
	logged_at?: string
	debug?: DamageDebug // temporary diagnostics — see DebugPanel
}

export type DamageAvailability = {
	hasHandover: boolean
	hasReturn: boolean
	beforePhotoCount: number
	afterPhotoCount: number
	canRun: boolean
	hasReport: boolean
	lastReportAt: string | null
}

const baseUrl = (reservationId: string) =>
	`/api/0/reservation/${encodeURIComponent(reservationId)}/damage-analysis`

const queryKeys = {
	availability: (id: string) => ['api', 'damage-analysis', 'availability', id] as const,
	report: (id: string) => ['api', 'damage-analysis', 'report', id] as const,
}

/**
 * Lightweight check used to decide whether the 'Vyhodnotit poškození'
 * tab should appear. Always returns shape — even when canRun is false.
 */
export function useDamageAnalysisAvailability(reservationId: string, enabled = true) {
	return useQuery({
		queryKey: queryKeys.availability(reservationId),
		queryFn: () => get<DamageAvailability>(`${baseUrl(reservationId)}/availability`),
		enabled: enabled && !!reservationId,
		// Refetch on focus so the tab appears as soon as workflow requests arrive.
		refetchOnWindowFocus: true,
		staleTime: 30_000,
	})
}

/**
 * Latest stored report (or 404 → null). Cheap to call repeatedly.
 */
export function useDamageReport(reservationId: string, enabled = true) {
	return useQuery({
		queryKey: queryKeys.report(reservationId),
		queryFn: async () => {
			try {
				return await get<DamageReport>(baseUrl(reservationId))
			} catch (err: unknown) {
				// 404 — no report yet — treat as null instead of failure.
				const error = err as { code?: string; message?: string }
				if (error?.code === 'DAMAGE_NO_REPORT' || /404/.test(error?.message || '')) {
					return null
				}
				throw err
			}
		},
		enabled: enabled && !!reservationId,
		staleTime: 60_000,
	})
}

/**
 * Run the analysis. Long-running. Invalidates the availability + report
 * queries on success so the UI re-fetches.
 */
export function useDamageAnalysisRun(reservationId: string) {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (variables: { minConfidence?: number; multiPass?: boolean }) =>
			post<DamageReport>(baseUrl(reservationId), {
				min_confidence: variables.minConfidence ?? 0,
				multi_pass: variables.multiPass,
			}),
		onSuccess: report => {
			queryClient.setQueryData(queryKeys.report(reservationId), report)
			queryClient.invalidateQueries({ queryKey: queryKeys.availability(reservationId) })
		},
	})
}

export type DamageStats = {
	total_reports: number
	total_damages: number
	range: { from: string | null; to: string | null }
	dimensions: {
		by_month: Record<string, number>
		by_severity: { 1: number; 2: number; 3: number }
		by_type: Record<string, number>
		by_angle: Record<string, number>
		by_vehicle: Record<string, number>
		by_client: Record<string, number>
	}
}

export function useDamageStats(filter: { from?: string; to?: string } = {}) {
	const params = new URLSearchParams()
	if (filter.from) params.set('from', filter.from)
	if (filter.to) params.set('to', filter.to)
	return useQuery({
		queryKey: ['api', 'damage-stats', filter.from || '', filter.to || ''],
		queryFn: () => get<DamageStats>('/api/0/damage-analysis-stats', params),
		staleTime: 60_000,
	})
}

/**
 * Send operator feedback for a single damage. Doesn't mutate the
 * displayed report — feedback only changes future analyses. We do
 * track which indices have been rejected client-side via React state.
 */
export function useDamageFeedback(reservationId: string) {
	return useMutation({
		mutationFn: (variables: {
			damageIndex: number
			action: 'reject' | 'confirm'
			reason?: string
			damageSnapshot: DamageItem
			activityLogId?: string
		}) =>
			post<{ ok: boolean }>(`${baseUrl(reservationId)}/feedback`, {
				damage_index: variables.damageIndex,
				action: variables.action,
				reason: variables.reason,
				damage_snapshot: variables.damageSnapshot,
				activity_log_id: variables.activityLogId,
			}),
	})
}
